import { z } from 'zod';
import { getActiveTrellusClient } from './host-runtime';

/**
 * Shared plumbing for v15 `ui.configure` / `ui.reset_configuration` writes (launcher hide, Bulk dial label).
 *
 * v15 contract (CLAY-TRELLUS-UI-CONFIGURATION.md §3): every component has a revision starting at 1; a write names
 * `expected_config_revision`; a stale one rejects with `configuration_revision_conflict` and the host must READ and
 * decide, never overwrite blindly. So each write is: `ui.get_configuration` → write with that revision → on a
 * conflict, re-read and retry exactly once. Writes never throw.
 */

const ConfigurationSnapshotSchema = z.object({ config_revision: z.number().int().min(1) });

/** Is `components.<id>.appearance.<field>` advertised with exactly this type (e.g. `boolean`, `text`)? */
export function isAppearanceFieldAdvertised(componentId: string, field: string, type: string): boolean {
  const component = getActiveTrellusClient()?.getCapabilities()?.components[componentId];
  return z.object({ appearance: z.object({ [field]: z.object({ type: z.literal(type) }) }) }).safeParse(component).success;
}

/**
 * One compare-and-set write against a component. `ui.configure` carries `appearance`; `ui.reset_configuration`
 * (restores the extension's defaults) carries none. Returns whether it applied.
 */
export async function writeComponentConfiguration(
  componentId: string,
  write: { command: 'ui.configure'; appearance: Record<string, unknown> } | { command: 'ui.reset_configuration' },
): Promise<boolean> {
  const client = getActiveTrellusClient();
  if (!client) return false;
  for (let attempt = 0; attempt < 2; attempt++) {
    const snapshot = await client.sendCommand('ui.get_configuration', { component_id: componentId });
    if (!snapshot.ok) return false;
    const parsed = ConfigurationSnapshotSchema.safeParse(snapshot.value);
    if (!parsed.success) return false;
    const result = await client.sendCommand(write.command, {
      component_id: componentId,
      expected_config_revision: parsed.data.config_revision,
      ...(write.command === 'ui.configure' ? { appearance: write.appearance } : {}),
    });
    if (result.ok) return true;
    if (result.reason !== 'rejected' || result.reason_code !== 'configuration_revision_conflict') return false;
  }
  return false;
}

/**
 * Writes are serialized on one promise chain so setup → cleanup → setup (React StrictMode, or a fast remount) can
 * never interleave a stale write after a newer one. The chain holds no lifetime state.
 */
let writeChain: Promise<unknown> = Promise.resolve();
export function enqueueUiWrite(task: () => Promise<unknown>): void {
  writeChain = writeChain.then(task, task).catch(() => undefined);
}
